import React, { useCallback, useEffect, useState } from "react";
import Button from "../../../components/common/button/Button";
import SearchableSelect from "../../../components/common/searchableSelect/SearchableSelect";
import TextField from "../../../components/common/textfield/TextField";
import {
  ValidateFields,
  ValidateInputs,
  ValidateNumberOrDecimal,
} from "../../../utils/formValidation";
import { itemTypeOptions } from "../../../consts/dropDownOptions/itemsOptions";
import { routeNames } from "../../../consts/routesNames";
import { useLocation, useNavigate, useParams } from "react-router";
import "./AddEditItems.css";
import UploadImageInput from "../../../components/common/uploadImageInput/UploadImageInput";
import { useDispatch } from "react-redux";
import { openModal } from "../../../redux/modal/modalActions";
import { getCookie } from "../../../utils/cookies";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { getAllPhases } from "../../../apis/phasesApis/getAllPhases";
import { getAllUnits } from "../../../apis/itemsApis/getAllUnits";
import { getAllContacts } from "../../../apis/contactsApis/getAllContacts";
import { getAllCompanies } from "../../../apis/companiesApis/getAllCompanies";
import { getSubPhasesbyId } from "../../../apis/phasesApis/getSubPhasesById";
import { createItem } from "../../../apis/itemsApis/createItem";
import { editItem } from "../../../apis/itemsApis/editItem";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useCrudPermissions } from "../../../auth/hooks/useCrudPermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";

function AddEditItems() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = getCookie("userId");
  const { canCreate, canUpdate } = useCrudPermissions(PERMISSIONS.ITEMS);
  const { canUpdateOwnedRecord, isProjectManager } = useUpdateOwnership();

  const { id: itemId } = useParams();
  const data = location?.state?.data;
  const editable = data && Object.entries(data).length > 0;
  const canSubmitItem = editable
    ? canUpdateOwnedRecord(data?.created_by, canUpdate)
    : canCreate;

  useEffect(() => {
    if (!editable || !isProjectManager) return;
    if (!canUpdateOwnedRecord(data?.created_by, canUpdate)) {
      dispatch(
        openModal(
          <ResponseModal
            text="You can only update items you created."
            isError={true}
          />,
        ),
      );
      navigate(routeNames.ITEMS);
    }
  }, [
    canUpdate,
    canUpdateOwnedRecord,
    data?.created_by,
    dispatch,
    editable,
    isProjectManager,
    navigate,
  ]);

  const [phasesData, setPhasesData] = useState([]);
  const [subPhasesData, setSubPhasesData] = useState([]);
  const [suppliersData, setSuppliersData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [unitsData, setUnitsData] = useState([]);
  const [supplyType, setSupplyType] = useState("company");

  // === Contact Info States ===
  const [name, setName] = useState("");
  const [phase, setPhase] = useState({ id: "", name: "" });
  const [subPhase, setSubPhase] = useState({ id: "", name: "" });
  const [supplier, setSupplier] = useState({ id: "", name: "" });
  const [company, setCompany] = useState({ id: "", name: "" });
  const [unit, setUnit] = useState({ id: "", name: "" });
  const [type, setType] = useState(""); // Fixed in the BE Model and FE as Options
  const [price, setPrice] = useState(null);
  const [SKU, setSKU] = useState("");
  const [phaseSelected, setPhaseSelected] = useState(false);
  const [itemPicture, setItemPicture] = useState("");

  // Load Options to DropDown

  useEffect(() => {
    const getPhases = async () => {
      try {
        const allPhasesRes = await getAllPhases();
        if (allPhasesRes.error_code === 2 || allPhasesRes.error_code === 3) {
          dispatch(
            openModal(
              <ResponseModal text={allPhasesRes.error_message} isError={true} />
            )
          );
        } else {
          setPhasesData(allPhasesRes.res.data);
        }
      } catch (error) {
        console.error("Error fetching Phases:", error);
      }
    };
    getPhases();
  }, [dispatch]);

  useEffect(() => {
    const getUnits = async () => {
      try {
        const allUnitRes = await getAllUnits();
        if (allUnitRes.error_code === 2 || allUnitRes.error_code === 3) {
          dispatch(
            openModal(
              <ResponseModal text={allUnitRes.error_message} isError={true} />
            )
          );
        } else {
          setUnitsData(allUnitRes.res.data);
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };
    getUnits();
  }, [dispatch]);

  useEffect(() => {
    const getSuppliers = async () => {
      try {
        const allContactRes = await getAllContacts();
        if (allContactRes.error_code === 2 || allContactRes.error_code === 3) {
          dispatch(
            openModal(
              <ResponseModal
                text={allContactRes.error_message}
                isError={true}
              />
            )
          );
        } else {
          const suppliersData = allContactRes.res.data.map((contact) => ({
            id: contact.id,
            name: `${contact.first_name} ${
              contact.last_name ? contact.last_name : ""
            }`,
          }));
          setSuppliersData(suppliersData);
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };
    const getCompanies = async () => {
      try {
        const allCompaniesRes = await getAllCompanies();
        if (
          allCompaniesRes.error_code === 2 ||
          allCompaniesRes.error_code === 3
        ) {
          dispatch(
            openModal(
              <ResponseModal
                text={allCompaniesRes.error_message}
                isError={true}
              />
            )
          );
        } else {
          const companiesData = allCompaniesRes.res.data.map((company) => ({
            id: company.id,
            name: company.name.trim(),
          }));
          setCompaniesData(companiesData);
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };
    getSuppliers();
    getCompanies();
  }, [dispatch]);

  const isRadioButtonSelected = (value) => supplyType === value;

  const getSubPhasesByPhaseId = useCallback(async (phaseId) => {
    try {
      const allSubPhasesRes = await getSubPhasesbyId(phaseId);
      if (
        allSubPhasesRes.error_code === 2 ||
        allSubPhasesRes.error_code === 3
      ) {
        dispatch(
          openModal(
            <ResponseModal
              text={allSubPhasesRes.error_message}
              isError={true}
            />
          )
        );
      } else {
        setSubPhasesData(allSubPhasesRes.res.data);
      }
    } catch (error) {
      console.error("Error fetching Sup Phases:", error);
    }
  }, [dispatch]);

  // Set All States
  useEffect(() => {
    if (editable) {
      setName(data.name);
      setPhase(data.phase);
      setSubPhase(data.sub_phase);
      setPhaseSelected(true);
      getSubPhasesByPhaseId(data.phase.id);
      if (data.supplier) {
        setSupplier(() => ({
          ...data.supplier,
          name: `${data.supplier.first_name ?? ""} ${data.supplier.last_name ?? ""}`.trim(),
        }));
      }
      if (data.company) {
        setCompany(() => ({
          ...data.company,
          name: (data.company.name ?? "").trim(),
        }));
      }
      setUnit(data.unit);
      setType(data.type);
      setPrice(data.price);
      setSKU(data.SKU);
      setItemPicture(data.item_picture);
      setSupplyType(data.supplier ? "supplier" : "company");
    }
  }, [data, editable, getSubPhasesByPhaseId]);

  // === Create - Edit ReqBody ====
  const reqBody = () => {
    const baseRequestBody = {
      name: name,
      type: type,
      SKU: SKU,
      price: price,
      phase_id: phase.id, // Have Logic
      sub_phase_id: subPhase.id,
      unit_id: unit.id,
      supplier_id: supplyType === "supplier" ? supplier.id : null,
      company_id: supplyType === "company" ? company.id : null,
      item_picture: itemPicture,
    };
    if (editable) {
      return {
        ...baseRequestBody,
        item_id: itemId,
        updated_by_user_id: userId,
      };
    } else {
      return {
        ...baseRequestBody,
        created_by_user_id: userId,
      };
    }
  };

  // === Add Handlers ===
  const addItemHandler = async () => {
    if (!canSubmitItem) return;
    const isValidated = addEditItemValidation();
    if (isValidated) {
      const createItemRes = await createItem(reqBody());
      if (createItemRes.error_code === 1) {
        alert("Item Created Successfully");
        navigate(routeNames.ITEMS);
      }
    }
  };

  // === Edit Handlers ===
  const editItemHandler = async (e) => {
    e.preventDefault();
    if (!canSubmitItem) return;
    const isValidated = addEditItemValidation();
    if (isValidated) {
      const editItemRes = await editItem(reqBody());
      if (editItemRes.error_code === 1) {
        alert("Item Updated Successfully");
        navigate(routeNames.ITEMS);
      }
    }
  };

  // === Image Handler ===
  const handleImageChange = (e) => {
    setItemPicture(e.target.files[0]);
  };

  // === Form Validation ===
  const addEditItemValidation = () => {
    if (!ValidateFields(name)) {
      alert("Item Name is required");
      return false;
    }
    if (!ValidateInputs(phase)) {
      alert("Phase is required");
      return false;
    }
    if (!ValidateInputs(subPhase)) {
      alert("Sub Phase is required");
      return false;
    }
    // if (!ValidateInputs(supplier)) {
    //   alert("Supplier is required");
    //   return false;
    // }
    if (!ValidateInputs(unit)) {
      alert("Unit is required");
      return false;
    }
    if (!validatePrice()) {
      return false;
    }
    return true;
  };

  const handlePriceChange = (value) => {
    setPrice(value);
  };

  const validatePrice = () => {
    if (price === null || price === "") {
      return true;
    }
    if (!ValidateNumberOrDecimal(price)) {
      alert("Price must be a valid number or decimal");
      return false;
    }
    return true;
  };

  return (
    <>
      <div className="main-container-addEditItems">
        {/* Inputs Container */}
        <div className="form-main-container-addEditItems">
          {/* Item Info */}

          <div className="form-column-addEditItems">
            <div className="form-card-text-container-addEditItems">
              Item Info
            </div>
            <div className="form-card-input-container-addEditItems">
              {/* Item Info Input fields */}
              <div className="labeltext-container-addEditItems">
                <TextField
                  label={"Name"}
                  name="name"
                  type="text"
                  setValue={setName}
                  value={name}
                  placeHolder=" Name ...."
                  isRequired={true}
                />
              </div>
              <div className="labeltext-container-addEditItems">
                <SearchableSelect
                  value={phase.name}
                  onChange={(e, newValue) => {
                    setPhase(newValue);
                    getSubPhasesByPhaseId(newValue.id);
                    setPhaseSelected(true);
                  }}
                  options={phasesData}
                  label={"Phase"}
                  name="phase"
                  // disabled={editable ? true : false}
                  required={true}
                />
              </div>
              <div className="labeltext-container-addEditItems">
                <SearchableSelect
                  value={subPhase.name}
                  onChange={(e, newValue) => {
                    setSubPhase(newValue);
                  }}
                  options={subPhasesData}
                  label={"Sub Phase"}
                  name="sub_phase"
                  disabled={phaseSelected ? false : true}
                  required={true}
                />
              </div>
              <div className="radio-container-addEditItems">
                <span className="label">Supply Type:</span>
                <div>
                  <input
                    className=""
                    type="radio"
                    name="supply_type"
                    value="supplier"
                    onChange={() => {
                      setSupplyType("supplier");
                    }}
                    checked={isRadioButtonSelected("supplier")}
                  />
                  <label className="label">Contact Supplier</label>
                </div>
                <div>
                  <input
                    className=""
                    type="radio"
                    name="supply_type"
                    value="company"
                    onChange={() => {
                      setSupplyType("company");
                    }}
                    checked={isRadioButtonSelected("company")}
                  />
                  <label className="label">Company Supplier</label>
                </div>
              </div>
              {supplyType === "supplier" && (
                <div className="labeltext-container-addEditItems">
                  <SearchableSelect
                    value={supplier.name}
                    onChange={(e, newValue) => {
                      setSupplier(newValue);
                    }}
                    options={suppliersData}
                    label={"Contact Supplier"}
                    name="supplier"
                    required={true}
                  />
                </div>
              )}
              {supplyType === "company" && (
                <div className="labeltext-container-addEditItems">
                  <SearchableSelect
                    value={company.name}
                    onChange={(e, newValue) => {
                      setCompany(newValue);
                    }}
                    options={companiesData}
                    label={"Company Supplier"}
                    name="company"
                    required={true}
                  />
                </div>
              )}
              <div className="labeltext-container-addEditItems">
                <SearchableSelect
                  value={unit.name}
                  onChange={(e, newValue) => {
                    setUnit(newValue);
                  }}
                  options={unitsData}
                  label={"Unit"}
                  name="unit"
                  // disabled={editable ? true : false}
                  required={true}
                />
              </div>
              <div className="labeltext-container-addEditItems">
                <SearchableSelect
                  value={type}
                  onChange={(e, newValue) => {
                    setType(e); 
                  }}
                  options={itemTypeOptions}
                  label={"Type"}
                  name="type"
                  // disabled={editable ? true : false}
                  required={true}
                />
              </div>
              <div className="labeltext-container-addEditItems">
                <TextField
                  label={"Price"}
                  name="price"
                  type="text"
                  setValue={handlePriceChange}
                  value={price === null ? "" : price}
                  placeHolder=" Price ...."
                  isRequired={true}
                />
              </div>
              <div className="labeltext-container-addEditItems">
                <TextField
                  label={"SKU"}
                  name="SKU"
                  type="text"
                  setValue={setSKU}
                  value={SKU}
                  placeHolder=" SKU ...."
                />
              </div>
              {!editable && (
                <div className="labeltext-container-addEditItems">
                  <UploadImageInput
                    name="Item Picture"
                    label="Item Picture"
                    onclick={(e) => handleImageChange(e)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Buttons Container */}
        <div className="button-main-container-addEditItems">
          <div className={"save-create-buttons-container"}>
            {editable ? (
              <div className="save-button-container">
                <Button
                  buttonText={"Save"}
                  onClick={(e) => editItemHandler(e)}
                  disabled={!canSubmitItem}
                />
              </div>
            ) : (
              <div className="create-button-container">
                <Button
                  buttonText={"Create"}
                  type="submit"
                  onClick={() => addItemHandler()}
                  disabled={!canSubmitItem}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AddEditItems;
